import {
  getStoreBlogByUserIdAndSlug,
  getStoreBlogPost,
  updateStoreBlogViewsKeepalive,
  updateStoreBlogsPost,
} from '../../util/api';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { buildStoreBlogSlug, getStoreBlogSlug } from '../../util/storeBlogs';

// Action types
export const FETCH_BLOG_REQUEST = 'app/StoreBlogContentPage/FETCH_BLOG_REQUEST';
export const FETCH_BLOG_SUCCESS = 'app/StoreBlogContentPage/FETCH_BLOG_SUCCESS';
export const FETCH_BLOG_ERROR = 'app/StoreBlogContentPage/FETCH_BLOG_ERROR';

export const SHOW_USER_REQUEST = 'app/StoreBlogContentPage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/StoreBlogContentPage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/StoreBlogContentPage/SHOW_USER_ERROR';

// Action creators
export const fetchBlogRequest = id => ({ type: FETCH_BLOG_REQUEST, payload: { id } });
export const fetchBlogSuccess = blog => ({ type: FETCH_BLOG_SUCCESS, payload: { blog } });
export const fetchBlogError = error => ({ type: FETCH_BLOG_ERROR, payload: error, error: true });

export const showUserRequest = id => ({ type: SHOW_USER_REQUEST, payload: { id } });
export const showUserSuccess = user => ({ type: SHOW_USER_SUCCESS, payload: { user } });
export const showUserError = error => ({ type: SHOW_USER_ERROR, payload: error, error: true });

// Initial state
const initialState = {
  blog: null,
  blogId: null,
  blogLoading: false,
  blogError: null,
  blogFetched: false,
  user: null,
  userId: null,
  userLoading: false,
  userError: null,
  userFetched: false,
};

// Reducer
const storeBlogContentPageReducer = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BLOG_REQUEST:
      return {
        ...state,
        blogId: payload.id,
        blog: state.blog?._id === payload.id ? state.blog : null,
        blogLoading: true,
        blogError: null,
        blogFetched: false,
      };
    case FETCH_BLOG_SUCCESS:
      return { ...state, blogLoading: false, blog: payload.blog, blogFetched: true };
    case FETCH_BLOG_ERROR:
      return { ...state, blogLoading: false, blog: null, blogError: payload, blogFetched: true };
    case SHOW_USER_REQUEST:
      return {
        ...state,
        userId: payload.id,
        user: state.user?.id?.uuid === payload.id ? state.user : null,
        userLoading: true,
        userError: null,
        userFetched: false,
      };
    case SHOW_USER_SUCCESS:
      return { ...state, userLoading: false, user: payload.user, userFetched: true };
    case SHOW_USER_ERROR:
      return { ...state, userLoading: false, user: null, userError: payload, userFetched: true };
    default:
      return state;
  }
};
export default storeBlogContentPageReducer;

// Thunk: fetch a blog by blogId
export const requestFetchBlog = blogId => async dispatch => {
  dispatch(fetchBlogRequest(blogId));
  try {
    const response = await getStoreBlogPost(blogId);
    // Flatten blog data for UI
    const blog = response.data;
    
    dispatch(fetchBlogSuccess(blog));
    return blog;
  } catch (e) {
    dispatch(fetchBlogError(storableError(e)))
  }
};

export const requestFetchBlogBySlug = (userId, slug) => async dispatch => {
  dispatch(fetchBlogRequest(`slug:${userId}:${slug}`));

  try {
    const response = await getStoreBlogByUserIdAndSlug(userId, slug);
    const blog = response?.data || null;

    dispatch(fetchBlogSuccess(blog));
    return blog;
  } catch (e) {
    dispatch(fetchBlogError(storableError(e)));
  }
};

export const ensureStoreBlogSlug = blog =>
  async (dispatch, getState) => {
    const blogId = blog?._id || blog?.id;
    const currentSlug = getStoreBlogSlug(blog);

    if (!blogId || currentSlug) {
      return currentSlug;
    }

    const nextSlug = buildStoreBlogSlug(blog);
    if (!nextSlug || nextSlug === 'no-slug') {
      return '';
    }

    try {
      const response = await updateStoreBlogsPost(blogId, { slug: nextSlug });
      const updatedBlog = response?.data || response || {};
      const existingBlog = getState().StoreBlogContentPage?.blog || blog;

      dispatch(
        fetchBlogSuccess({
          ...existingBlog,
          ...updatedBlog,
          slug: updatedBlog?.slug || nextSlug,
        })
      );

      return updatedBlog?.slug || nextSlug;
    } catch (e) {
      return '';
    }
  };

// Thunk: increment blog views and clicks
export const incrementBlogViewsAndClicks = (blogId, incrementClicks = false) => async (dispatch, getState) => {
  try {
    const { blog } = getState().StoreBlogContentPage || {};
    if(!blog) return;
    const newViews = (blog.views || 0) + 1;
    const newClicks = incrementClicks ? (blog.clicks || 0) + 1 : blog.clicks;
    updateStoreBlogViewsKeepalive(blogId, { views: newViews, clicks: newClicks });
    // Optionally, you can re-fetch the blog or optimistically update state here
  } catch (e) {
    // Optionally handle error
    // console.error('Failed to increment blog views/clicks', e);
  }
};

// Thunk: fetch a user by userId
export const requestShowUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = (config?.layout?.listingImage || {});

  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: userId,
    include: ['profileImage'],
    'fields.image': [
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-medium',
      'variants.square-medium2x',
      'variants.square-large',
      'variants.square-large2x',
      'variants.square-xlarge',
      'variants.square-xlarge2x',
    ],
  };

  return sdk.users.show(params)
    .then(res => {
      const user = denormalisedResponseEntities(res)[0];
      dispatch(showUserSuccess(user));
      return user;
    })
    .catch(e => {
      dispatch(showUserError(storableError(e)));
    });
};

// Thunk: loadData for SSR + client-side route transitions
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { blogId, userId, slug } = params || {};

  const state = getState().StoreBlogContentPage || {};
  const stateBlogId = state.blogId || state.blog?._id || state.blog?.id;
  const stateBlogSlug = getStoreBlogSlug(state.blog);
  const stateUserId = state.userId || state.user?.id?.uuid || state.user?.id;

  const shouldFetchBlog =
    (blogId && !(stateBlogId === blogId && !state.blogLoading && !state.blogError)) ||
    (slug &&
      userId &&
      !(
        stateBlogSlug === slug &&
        stateUserId === userId &&
        !state.blogLoading &&
        !state.blogError
      ));
  const shouldFetchUser =
    userId && !(stateUserId === userId && !state.userLoading && !state.userError);

  const blogPromise = shouldFetchBlog
    ? blogId
      ? dispatch(requestFetchBlog(blogId))
      : dispatch(requestFetchBlogBySlug(userId, slug))
    : Promise.resolve(state.blog);
  const userPromise = shouldFetchUser ? dispatch(requestShowUser(userId, config)) : Promise.resolve(state.user);

  return Promise.all([blogPromise, userPromise]);
};
